import { AHIcon, AHText, Box, ListItem, TouchableBox } from "arkh";
import React, { useCallback } from "react";
import { graphql, useFragment } from "react-relay";
import { IssueBadge } from "./IssueBadge";
import { PillIndicator } from "../../PillIndicator";
import { WorkspaceRoutineItemRowContainer } from "./WorkspaceRoutineItemRowContainer";
import { WorkspaceRoutinesListItemSchedule } from "./WorkspaceRoutinesListItemSchedule";
import { WorkspaceRoutinesListItemStatus_RoutineWorkspaceOccasion$key } from "./__generated__/WorkspaceRoutinesListItemStatus_RoutineWorkspaceOccasion.graphql";
import { ListItemDescriptionContent } from "../../../Team/TeamHomeNavigator/TeamScheduleListItem/ListItemDescriptionContent";
import { WorkspaceRoutinesListItemContent_routine$key } from "./__generated__/WorkspaceRoutinesListItemContent_routine.graphql";
import { WorkspaceRoutinesListItem_Routine$key } from "./__generated__/WorkspaceRoutinesListItem_Routine.graphql";
import { WorkspaceDifferentOwnerIndicator } from "../../WorkspaceDifferentOwnerIndicator";
import { View } from "react-native";
import { StoresIndicator } from "../../WorkspaceTodos/StoresIndicator";

function WorkspaceRoutinesListItemStatus(props: {
  workspaceOccasion: WorkspaceRoutinesListItemStatus_RoutineWorkspaceOccasion$key | null;
}) {
  const workspaceOccasion = useFragment(
    graphql`
      fragment WorkspaceRoutinesListItemStatus_RoutineWorkspaceOccasion on RoutineWorkspaceOccasion {
        storesCompletedCount
        storesCount
      }
    `,
    props.workspaceOccasion
  );

  if (!workspaceOccasion) return null;

  const percentage =
    (workspaceOccasion.storesCompletedCount / workspaceOccasion.storesCount) *
    100;

  return (
    <View>
      <StoresIndicator
        status={"ACTIVE"}
        totalStores={workspaceOccasion.storesCount}
        value={workspaceOccasion.storesCompletedCount}
      />
      <Box spaceTop="xsmall">
        <PillIndicator percentage={percentage} />
      </Box>
    </View>
  );
}

export function WorkspaceRoutinesListItem(props: {
  workspaceRoutine: WorkspaceRoutinesListItem_Routine$key;
  isLast: boolean;
  openRoutineOccasion: (routineId: string, sequenceNumber: number) => void;
  openRoutineEdit: (routineId: string) => void;
  showTopics: boolean;
}) {
  const item = useFragment(
    graphql`
      fragment WorkspaceRoutinesListItem_Routine on Routine {
        id
        isWorkspaceOwner(workspaceId: $workspaceId)
        workspaceOwnerName(workspaceId: $workspaceId)
        ...WorkspaceRoutinesListItemContent_routine
        inProgressOrNextWorkspaceOccasion(workspaceId: $workspaceId) {
          ...WorkspaceRoutinesListItemStatus_RoutineWorkspaceOccasion
          sequenceNumber
          issueCount
        }
        orgTopic {
          name
        }
        currentSchedule {
          ...WorkspaceRoutinesListItemSchedule_schedule
        }
      }
    `,
    props.workspaceRoutine
  );

  const onPress = useCallback(() => {
    if (item.inProgressOrNextWorkspaceOccasion) {
      props.openRoutineOccasion(
        item.id,
        item.inProgressOrNextWorkspaceOccasion.sequenceNumber
      );
    }
  }, [item.id, item.inProgressOrNextWorkspaceOccasion, props]);

  return (
    <ListItem
      displayMode="TABLE"
      border="border-primary"
      isLast={props.isLast}
      disabled={!item.inProgressOrNextWorkspaceOccasion}
      onPress={onPress}
    >
      <WorkspaceDifferentOwnerIndicator name={item.workspaceOwnerName} />
      <WorkspaceRoutineItemRowContainer
        title={<WorkspaceRoutinesListItemContent routine={item} />}
        freq={
          <WorkspaceRoutinesListItemSchedule schedule={item.currentSchedule} />
        }
        issueCount={
          item.inProgressOrNextWorkspaceOccasion ? (
            <IssueBadge
              size="smallMedium"
              count={item.inProgressOrNextWorkspaceOccasion?.issueCount}
            />
          ) : null
        }
        status={
          <WorkspaceRoutinesListItemStatus
            workspaceOccasion={item.inProgressOrNextWorkspaceOccasion}
          />
        }
        topic={
          props.showTopics ? (
            <AHText numberOfLines={1}>{item.orgTopic?.name ?? ""}</AHText>
          ) : null
        }
        more={
          item.isWorkspaceOwner ? (
            <TouchableBox
              onPress={() => props.openRoutineEdit(item.id)}
              style={{
                flex: 1,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <AHIcon type="more-vertical" color="darkText" />
            </TouchableBox>
          ) : (
            <AHIcon type="locked" color="darkText" size="xsmall" />
          )
        }
      />
    </ListItem>
  );
}

export function WorkspaceRoutinesListItemContent(props: {
  routine: WorkspaceRoutinesListItemContent_routine$key;
}) {
  const routine = useFragment(
    graphql`
      fragment WorkspaceRoutinesListItemContent_routine on Routine {
        title
        description
        descriptionImages {
          ...ListItemDescriptionContent_images
        }
        descriptionFiles {
          ...ListItemDescriptionContentFiles_files
        }
      }
    `,
    props.routine
  );

  return (
    <ListItemDescriptionContent
      files={routine.descriptionFiles}
      images={routine.descriptionImages}
      title={routine.title}
      description={routine.description}
      products={[]}
    />
  );
}
